import { pipe, Option } from 'effect';
import { selector } from '@/lib/selectors';
import type { RecordOf } from 'immutable';

import type { EnvState, CompanyPermissionsEntry } from '@/domain/env/types';
import type { Role, RoleModificators } from '@/domain/env/schemas';
import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';

// The signed-in user's role block exists in two scopes.
//
//   organization* — the organization defaults. Use these for anything written
//                   back to the organization record, such as the profile save.
//   company*      — what applies in the company in scope: that company's
//                   override where one is stored, the organization values
//                   otherwise. Use these for permissions.
//
// There is deliberately no unscoped role selector: a value read in one scope
// and written into the other corrupts the organization record silently.

const env = (state) => state.env;

export const organizationRoleSelector = selector<unknown, Role | null>(env, (e: EnvState) => e.get('role'));
export const organizationRoleModificatorsSelector = selector<unknown, RecordOf<RoleModificators>>(env, (e: EnvState) =>
  e.get('roleModificators'),
);

// Map.get hands back the stored Record, so the reference changes only when the
// override itself does — which keeps the permission mask memoized.
const companyOverrideSelector = selector<unknown, CompanyPermissionsEntry | undefined>(
  env,
  currentCompanyIdSelector,
  (e: EnvState, companyId: string | null): CompanyPermissionsEntry | undefined =>
    pipe(
      Option.fromNullable(companyId),
      Option.flatMap((id) => Option.fromNullable(e.get('companyPermissions').get(id))),
      Option.getOrUndefined,
    ),
);

// The role and the modificators always come from the same block: mixing an
// override's role with the organization's modificators yields a combination
// neither scope defines.
export const companyRoleSelector = selector<unknown, Role | null>(
  organizationRoleSelector,
  companyOverrideSelector,
  (organizationRole: Role | null, override: CompanyPermissionsEntry | undefined) =>
    override ? override.role : organizationRole,
);
// With an override stored, this is the whole override block: the role and the
// display role come along with the modificators.
export const companyRoleModificatorsSelector = selector<unknown, RecordOf<RoleModificators> | CompanyPermissionsEntry>(
  organizationRoleModificatorsSelector,
  companyOverrideSelector,
  (organizationModificators: RecordOf<RoleModificators>, override: CompanyPermissionsEntry | undefined) =>
    override ?? organizationModificators,
);

// Set when the server announces a change to the signed-in user's permissions; only a reload clears it.
export const isPermissionsChangedSelector = selector<unknown, boolean>(env, (e: EnvState) =>
  e.get('isPermissionsChanged'),
);
